import React from 'react';
import { VolatilityResponse } from '../types/StockData';

interface VolatilityTabProps {
  volatilityData: VolatilityResponse | null;
  volatilityLoading: boolean;
}

const VolatilityTab: React.FC<VolatilityTabProps> = ({ volatilityData, volatilityLoading }) => {
  if (volatilityLoading) {
    return (
      <div className="text-center py-4 text-gray-500">
        Loading volatility data...
      </div>
    );
  }

  if (!volatilityData) {
    return (
      <div className="text-center py-4 text-gray-500">
        No volatility data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Enhanced Signal */}
      <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded">
        <h4 className="font-semibold text-purple-800 mb-2">Enhanced Volatility Signal</h4>
        <p className="text-sm text-purple-700 mb-2">{volatilityData.enhancedVolSignal}</p>
        <div className="flex items-center gap-4 text-xs">
          <span className="font-medium">Risk Level:</span>
          <span className={`px-2 py-1 rounded ${
            volatilityData.riskLevel.includes('High') ? 'bg-red-100 text-red-800' :
            volatilityData.riskLevel.includes('Medium') ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {volatilityData.riskLevel}
          </span>
        </div>
      </div>

      {/* Volatility Stats */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <h4 className="font-semibold text-blue-800 mb-3">Volatility Analysis</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="font-medium text-gray-700">IV:</span>
            <div className="text-blue-800">{(volatilityData.volStats.iv * 100).toFixed(1)}%</div>
          </div>
          <div>
            <span className="font-medium text-gray-700">IV Rank:</span>
            <div className="text-blue-800">{volatilityData.volStats.iv_rank.toFixed(1)}%ile</div>
          </div>
          <div>
            <span className="font-medium text-gray-700">RV:</span>
            <div className="text-blue-800">{(volatilityData.volStats.rv * 100).toFixed(1)}%</div>
          </div>
          <div>
            <span className="font-medium text-gray-700">Vol Premium:</span>
            <div className={`${
              volatilityData.volStats.volatilityPremium > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {volatilityData.volStats.volatilityPremium > 0 ? '+' : ''}{(volatilityData.volStats.volatilityPremium * 100).toFixed(1)}%
            </div>
          </div>
        </div>
        <div className="mt-3 flex gap-4 text-xs">
          <span className={`px-2 py-1 rounded ${
            volatilityData.volStats.volatilityRegime.includes('High') ? 'bg-red-100 text-red-800' :
            volatilityData.volStats.volatilityRegime.includes('Low') ? 'bg-green-100 text-green-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {volatilityData.volStats.volatilityRegime}
          </span>
          <span className={`px-2 py-1 rounded ${
            volatilityData.volStats.premiumRegime.includes('Premium') ? 'bg-red-100 text-red-800' :
            volatilityData.volStats.premiumRegime.includes('Discount') ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {volatilityData.volStats.premiumRegime}
          </span>
        </div>
      </div>

      {/* Sentiment & Skew */}
      <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
        <h4 className="font-semibold text-green-800 mb-3">Sentiment Analysis</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="font-medium text-gray-700">Current Regime:</span>
            <div className="text-green-800">{volatilityData.currentSentimentRegime}</div>
          </div>
          <div>
            <span className="font-medium text-gray-700">Trend:</span>
            <div className="text-green-800">{volatilityData.sentimentTrend}</div>
          </div>
          <div>
            <span className="font-medium text-gray-700">Risk Reversal:</span>
            <div className="text-green-800">{(volatilityData.currentSkew.risk_reversal * 100).toFixed(2)}%</div>
          </div>
          <div>
            <span className="font-medium text-gray-700">Delta:</span>
            <div className="text-green-800">{volatilityData.currentSkew.delta}</div>
          </div>
        </div>
      </div>

      {/* Enhanced Summary */}
      <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded">
        <h4 className="font-semibold text-gray-800 mb-2">Summary</h4>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{volatilityData.enhancedSummary}</p>
        {volatilityData.compAnalysis && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{volatilityData.compAnalysis}</p>
        )}
      </div>
    </div>
  );
};

export default VolatilityTab;
